import React, { useState, useEffect } from "react";
import { withNamespaces } from "react-i18next";
import {
  ToggleButton,
  ToggleButtonGroup,
  Grid,
  Paper,
  Button,
  Stack,
  FormControl,
  InputLabel,
  Select as Selects,
  MenuItem,
  TextField,
  Menu,
  Card,
  CardContent,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  Pagination,
  Box,
  CircularProgress,
  Avatar,
  Chip,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { tableCellClasses } from "@mui/material/TableCell";
import Axios from "axios";
import Sidebar from "../../sidebar/sidebar";
import { companylogo } from "../../../assets/images";
import { BaseUrl } from "../../../Environment";
import moment from "moment";
import { FilterAlt, CalendarMonth, Person } from "@mui/icons-material";
import { getSecureItem } from "../../../utilsFunctions";
import PdfReportSavingsCollection from "./PdfReportSavingsCollection";
import excel from "../../../assets/images/excel.svg";
import pdf from "../../../assets/images/pdf.svg";
import { ExcelReport } from "../ExcelReport";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "#e1f0fa",
    color: theme.palette.common.black,
    fontWeight: "bold",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(even)": {
    backgroundColor: theme.palette.action.hover,
  },
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

const STATUS_COLOR = {
  Paid: "#1976d2",
  NotPaid: "#d32f2f",
  PartiallyPaid: "#c026d3",
};

const TenureFilter = [
  { value: "All", label: "All" },
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "biweekly", label: "Bi-Weekly" },
  { value: "monthly", label: "Monthly" },
];

const PaidFilter = [
  { value: "All", label: "All" },
  { value: "Paid", label: "Paid" },
  { value: "NotPaid", label: "Not Paid" },
];

// Small percentage ring, e.g. "103%" over a green circular track — mirrors the
// summary cards in the reference screenshot.
const PercentRing = ({ percent }) => (
  <Box sx={{ position: "relative", display: "inline-flex" }}>
    <CircularProgress
      variant="determinate"
      value={100}
      size={64}
      thickness={4}
      sx={{ color: "#e0e0e0" }}
    />
    <CircularProgress
      variant="determinate"
      value={Math.min(percent, 100)}
      size={64}
      thickness={4}
      sx={{ color: "#2e7d32", position: "absolute", left: 0 }}
    />
    <Box
      sx={{
        position: "absolute",
        inset: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <span style={{ fontSize: 13, fontWeight: 600 }}>{percent}%</span>
    </Box>
  </Box>
);

const SavingsCollectionReport = () => {
  const loggedinUser = getSecureItem("loggedinUser");
  const columnWidths = [{ wch: 200 }, { wch: 200 }, { wch: 250 }];

  const fullReportRef = React.useRef(false);
  const [fullReport, setFullReport] = useState(false);
  const setFullReportSync = (val) => {
    fullReportRef.current = val;
    setFullReport(val);
  };

  const [collectionData, setCollectionData] = useState([]);
  const [totalSummary, setTotalSummary] = useState(null);
  const [totalPages, setTotalPages] = useState(1);
  const [gotFullData, setGotFullData] = useState(false);
  const [loading, setLoading] = useState(true);
  const [exportType, setExportType] = useState(null);

  const [page, setPage] = useState(1);
  const [tenureType, setTenureType] = useState("All");
  const [paidStatus, setPaidStatus] = useState("All");
  const [areaLine, setAreaLine] = useState("");
  const [dataAreaLine, setDataAreaLine] = useState([]);
  const [cAgentId, setCAgentId] = useState("");
  const [cAgentData, setCAgentData] = useState([]);
  const [search, setSearch] = useState("");

  const [startDateD, setStartDateD] = useState(
    moment().startOf("year").format("YYYY-MM-DD HH:mm:ss"),
  );
  const [endDateD, setEndDateD] = useState(
    moment().endOf("year").format("YYYY-MM-DD HH:mm:ss"),
  );
  const [anchorElDate, setAnchorElDate] = useState(null);

  const userType = loggedinUser?.userType || "";

  const buildPayload = () => ({
    companyId: getSecureItem("loggedinUserCompany"),
    collectorId: cAgentId || (userType === "Collection Agent" ? loggedinUser?.userId : ""),
    tenureType,
    paidStatus,
    areaLine,
    startDateD,
    endDateD,
    search,
    page,
    limit: 20,
    fullReport: fullReportRef.current,
  });

  const getVerifiedCustomers = async () => {
    try {
      const companyId = getSecureItem("loggedinUserCompany");
      const { data } = await Axios.get(
        `${BaseUrl}/customer/getVerifiedCustomers?name=&companyId=${companyId}`,
        { headers: { Authorization: "Bearer " + getSecureItem("token") } },
      );
      const areas = data?.filter((v) => v?.areaLine).map((v) => v.areaLine);
      setDataAreaLine([...new Set(areas)]);
    } catch (err) {
      console.error(err);
    }
  };

  const getAllUsers = async () => {
    try {
      const companyId = getSecureItem("loggedinUserCompany");
      const { data } = await Axios.get(
        `${BaseUrl}/user/getAllUsers?userName=&companyId=${companyId}`,
        { headers: { Authorization: "Bearer " + getSecureItem("token") } },
      );
      const users = data?.result;
      setCAgentData(
        userType === "Collection Agent"
          ? users?.filter((u) => u.userId === loggedinUser?.userId)
          : users,
      );
    } catch (err) {
      console.error(err);
    }
  };

  const getCollectionData = async () => {
    try {
      setLoading(true);
      const token = getSecureItem("token");
      const res = await Axios.post(
        `${BaseUrl}/savings/getSavingsCollectionReport`,
        buildPayload(),
        { headers: { Authorization: "Bearer " + token } },
      );
      setCollectionData(res?.data?.result || []);
      setTotalPages(Math.ceil((res?.data?.totalRecords || 0) / 20) || 1);
      if (fullReportRef.current) setGotFullData(true);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const getCollectionSummary = async () => {
    try {
      const token = getSecureItem("token");
      const { data } = await Axios.post(
        `${BaseUrl}/savings/getSavingsCollectionSummary`,
        buildPayload(),
        { headers: { Authorization: "Bearer " + token } },
      );
      setTotalSummary(data?.data);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    getVerifiedCustomers();
    getAllUsers();
  }, []);

  useEffect(() => {
    getCollectionData();
    getCollectionSummary();
  }, [
    page,
    tenureType,
    paidStatus,
    areaLine,
    cAgentId,
    startDateD,
    endDateD,
    search,
    fullReport,
  ]);

  useEffect(() => {
    if (!gotFullData || !collectionData.length) return;

    if (exportType === "pdf") {
      PdfReportSavingsCollection({
        collectionData,
        totalSummary,
        tenureType,
        paidStatus,
        startDateD,
        endDateD,
      });
    }

    if (exportType === "excel") {
      const rows = collectionData.map((item) => ({
        Customer:
          `${item?.customer?.firstName ?? ""} ${item?.customer?.lastName ?? ""}`.trim() +
          (item?.customer?.cust_id ? ` (${item.customer.cust_id})` : ""),
        "Saving ID": item?.saving?.savingId,
        "Collection Agent": item?.user
          ? `${item.user.firstName} ${item.user.lastName}`
          : "",
        "Total Amount": item?.saving?.totalAmount,
        Installment: item.Installment,
        "Collection Amount": item.collectionAmount,
        "Due Date": item?.dueDate
          ? moment(item.dueDate).format("DD/MM/YYYY HH:mm:ss")
          : "",
        "Collected Date": item?.collectedDate
          ? moment(item.collectedDate).format("DD/MM/YYYY HH:mm:ss")
          : "",
        "Collected Seq": item?.saving?.tenureType,
        Penalty: item.penaltyApplied,
        "Saving Status": item?.saving?.savingStatus,
      }));
      ExcelReport({
        data: rows,
        fileName: "savings_collection_report.xlsx",
        sheetName: "Savings Collection Report",
        columnWidths,
      });
    }

    setTimeout(() => {
      setExportType(null);
      setFullReportSync(false);
      setGotFullData(false);
    }, 100);
  }, [gotFullData]);

  const handlePdfClick = () => {
    setExportType("pdf");
    setFullReportSync(true);
  };

  const handleExcelClick = () => {
    setExportType("excel");
    setFullReportSync(true);
  };

  const applyDatePreset = (preset) => {
    if (preset === "year") {
      setStartDateD(moment().startOf("year").format("YYYY-MM-DD HH:mm:ss"));
      setEndDateD(moment().endOf("year").format("YYYY-MM-DD HH:mm:ss"));
    }
    if (preset === "month") {
      setStartDateD(moment().startOf("month").format("YYYY-MM-DD HH:mm:ss"));
      setEndDateD(moment().endOf("month").format("YYYY-MM-DD HH:mm:ss"));
    }
    if (preset === "today") {
      setStartDateD(moment().startOf("day").format("YYYY-MM-DD HH:mm:ss"));
      setEndDateD(moment().endOf("day").format("YYYY-MM-DD HH:mm:ss"));
    }
    setPage(1);
    setAnchorElDate(null);
  };

  return (
    <>
      <Sidebar />
      <div style={{ marginLeft: "73px", height: "100vh" }}>
        <Grid container direction="column">
          <Grid item sx={{ backgroundColor: "#fff" }}>
            <div style={{ margin: 5 }}>
              <img src={companylogo} width={200} height={40} alt="logo" />
            </div>
          </Grid>

          {/* Filter row 1: tenure toggle, collection agent, date range, search */}
          <Grid
            item
            container
            paddingX={4}
            paddingY={2}
            spacing={2}
            alignItems="center"
          >
            <Grid item>
              <ToggleButtonGroup
                color="primary"
                value={tenureType}
                exclusive
                onChange={(_, val) => {
                  if (val) {
                    setPage(1);
                    setTenureType(val);
                  }
                }}
                size="small"
              >
                {TenureFilter.map((f, i) => (
                  <ToggleButton key={i} value={f.value}>
                    {f.label}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Grid>

            <Grid item xs={12} sm={3} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel id="agent-select">
                  <Person fontSize="small" sx={{ verticalAlign: "middle", mr: 0.5 }} />
                  Collection Agent
                </InputLabel>
                <Selects
                  labelId="agent-select"
                  label="Collection Agent"
                  value={cAgentId}
                  onChange={(e) => {
                    setPage(1);
                    setCAgentId(e.target.value);
                  }}
                >
                  <MenuItem value="">All</MenuItem>
                  {cAgentData.map((u) => (
                    <MenuItem key={u.userId} value={u.userId}>
                      {u.firstName} {u.lastName}
                    </MenuItem>
                  ))}
                </Selects>
              </FormControl>
            </Grid>

            <Grid item>
              <Button
                variant="outlined"
                startIcon={<CalendarMonth />}
                onClick={(e) => setAnchorElDate(e.currentTarget)}
              >
                {moment(startDateD).format("DD MMM YYYY HH:mm:ss")} -{" "}
                {moment(endDateD).format("DD MMM YYYY HH:mm:ss")}
              </Button>
              <Menu
                anchorEl={anchorElDate}
                open={Boolean(anchorElDate)}
                onClose={() => setAnchorElDate(null)}
              >
                <Stack spacing={2} padding={2} minWidth={260}>
                  <Stack direction="row" spacing={1}>
                    <Chip label="Today" onClick={() => applyDatePreset("today")} clickable />
                    <Chip label="This Month" onClick={() => applyDatePreset("month")} clickable />
                    <Chip label="This Year" onClick={() => applyDatePreset("year")} clickable />
                  </Stack>
                  <TextField
                    label="Start Date"
                    type="date"
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    value={startDateD}
                    onChange={(e) => setStartDateD(e.target.value)}
                  />
                  <TextField
                    label="End Date"
                    type="date"
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    value={endDateD}
                    onChange={(e) => setEndDateD(e.target.value)}
                  />
                  <Button
                    variant="contained"
                    onClick={() => {
                      setPage(1);
                      setAnchorElDate(null);
                    }}
                  >
                    Apply
                  </Button>
                </Stack>
              </Menu>
            </Grid>

            <Grid item xs={12} sm={3} md={2}>
              <TextField
                label="Search Customer"
                size="small"
                fullWidth
                value={search}
                onChange={(e) => {
                  setPage(1);
                  setSearch(e.target.value);
                }}
              />
            </Grid>
          </Grid>

          {/* Filter row 2: paid status, area/line */}
          <Grid
            item
            container
            paddingX={4}
            spacing={2}
            alignItems="center"
          >
            <Grid item>
              <ToggleButtonGroup
                color="primary"
                value={paidStatus}
                exclusive
                onChange={(_, val) => {
                  if (val) {
                    setPage(1);
                    setPaidStatus(val);
                  }
                }}
                size="small"
              >
                {PaidFilter.map((f, i) => (
                  <ToggleButton key={i} value={f.value}>
                    {f.label}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Grid>

            <Grid item xs={12} sm={3} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel id="area-select">
                  <FilterAlt fontSize="small" sx={{ verticalAlign: "middle", mr: 0.5 }} />
                  Area/Line
                </InputLabel>
                <Selects
                  labelId="area-select"
                  label="Area/Line"
                  value={areaLine}
                  onChange={(e) => {
                    setPage(1);
                    setAreaLine(e.target.value);
                  }}
                >
                  <MenuItem value="">All</MenuItem>
                  {dataAreaLine.map((a, i) => (
                    <MenuItem key={i} value={a}>
                      {a}
                    </MenuItem>
                  ))}
                </Selects>
              </FormControl>
            </Grid>
          </Grid>

          {/* Summary cards */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2,1fr)",
                md: "repeat(4,1fr)",
              },
              gap: 3,
              px: 4,
              py: 2,
            }}
          >
            <Card elevation={3} sx={{ borderRadius: 3 }}>
              <CardContent sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <PercentRing percent={totalSummary?.collectionPercent ?? 0} />
                <div>
                  <div style={{ fontSize: 13, color: "#666" }}>
                    Saving Collection Amount
                  </div>
                  <div style={{ fontSize: 22, fontWeight: 700 }}>
                    {totalSummary?.collectedAmount ?? 0}
                  </div>
                  <div style={{ fontSize: 12, color: "#999" }}>
                    Out of {totalSummary?.targetAmount ?? 0}
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card elevation={3} sx={{ borderRadius: 3 }}>
              <CardContent sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <PercentRing percent={totalSummary?.paidPercent ?? 0} />
                <div>
                  <div style={{ fontSize: 13, color: "#666" }}>Paid Collections</div>
                  <div style={{ fontSize: 22, fontWeight: 700 }}>
                    {totalSummary?.paidCount ?? 0}
                  </div>
                  <div style={{ fontSize: 12, color: "#999" }}>
                    Out of {totalSummary?.totalCollections ?? 0}
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card elevation={3} sx={{ borderRadius: 3 }}>
              <CardContent>
                <div style={{ fontSize: 13, color: "#666" }}>Overdue Amount</div>
                <div style={{ fontSize: 26, fontWeight: 700, marginTop: 8 }}>
                  {totalSummary?.overdueAmount ?? 0}
                </div>
              </CardContent>
            </Card>

            <Card elevation={3} sx={{ borderRadius: 3 }}>
              <CardContent>
                <div style={{ fontSize: 13, color: "#666" }}>Penalty</div>
                <div style={{ fontSize: 26, fontWeight: 700, marginTop: 8 }}>
                  {totalSummary?.totalPenalty ?? 0}
                </div>
              </CardContent>
            </Card>
          </Box>

          {/* Table */}
          <Grid item container paddingX={4} paddingY={2}>
            <Grid
              container
              justifyContent="space-between"
              alignItems="center"
              paddingBottom={1}
            >
              <Grid item>
                <p className="Fonts">{totalSummary?.totalCollections ?? 0} Collections</p>
              </Grid>
              <Grid item>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <Box sx={{ width: 10, height: 10, borderRadius: "50%", backgroundColor: STATUS_COLOR.Paid }} />
                      <span style={{ fontSize: 12 }}>Paid</span>
                    </Stack>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <Box sx={{ width: 10, height: 10, borderRadius: "50%", backgroundColor: STATUS_COLOR.NotPaid }} />
                      <span style={{ fontSize: 12 }}>Not Paid</span>
                    </Stack>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <Box sx={{ width: 10, height: 10, borderRadius: "50%", backgroundColor: STATUS_COLOR.PartiallyPaid }} />
                      <span style={{ fontSize: 12 }}>Partially Paid</span>
                    </Stack>
                  </Stack>
                  <span style={{ fontSize: 12, color: "#666" }}>Export to:</span>
                  <img src={pdf} alt="pdf" className="pdficon" onClick={handlePdfClick} style={{ cursor: "pointer" }} />
                  <img src={excel} alt="excel" className="excelicon" onClick={handleExcelClick} style={{ cursor: "pointer" }} />
                </Stack>
              </Grid>
            </Grid>

            <TableContainer component={Paper}>
              <Table sx={{ minWidth: 900 }}>
                <TableHead>
                  <TableRow>
                    <StyledTableCell>Customer Name</StyledTableCell>
                    <StyledTableCell>Saving ID</StyledTableCell>
                    <StyledTableCell>Collection Agent</StyledTableCell>
                    <StyledTableCell>Total Amount</StyledTableCell>
                    <StyledTableCell>Installment</StyledTableCell>
                    <StyledTableCell>Collection Amount</StyledTableCell>
                    <StyledTableCell>Due Date</StyledTableCell>
                    <StyledTableCell>Collected Date</StyledTableCell>
                    <StyledTableCell>Collected Seq</StyledTableCell>
                    <StyledTableCell>Penalty</StyledTableCell>
                    <StyledTableCell>Saving Status</StyledTableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <StyledTableCell colSpan={11} align="center">
                        <CircularProgress size={24} />
                      </StyledTableCell>
                    </TableRow>
                  ) : (
                    collectionData.map((data, index) => (
                      <StyledTableRow key={index}>
                        <StyledTableCell>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Avatar sx={{ width: 28, height: 28, fontSize: 13 }}>
                              {data?.customer?.firstName?.[0]}
                            </Avatar>
                            <span>
                              {data?.customer?.firstName} {data?.customer?.lastName}
                            </span>
                          </Stack>
                        </StyledTableCell>
                        <StyledTableCell>{data?.saving?.savingCode}</StyledTableCell>
                        <StyledTableCell>
                          {data?.user ? `${data.user.firstName} ${data.user.lastName}` : ""}
                        </StyledTableCell>
                        <StyledTableCell>{data?.saving?.totalAmount}</StyledTableCell>
                        <StyledTableCell>{data?.Installment}</StyledTableCell>
                        <StyledTableCell
                          sx={{ color: STATUS_COLOR[data?.collectionStatus] || "inherit", fontWeight: 600 }}
                        >
                          {data?.collectionAmount ?? 0}
                        </StyledTableCell>
                        <StyledTableCell>
                          {data?.dueDate ? moment(data.dueDate).format("DD/MM/YYYY") : ""}
                        </StyledTableCell>
                        <StyledTableCell>
                          {data?.collectedDate ? moment(data.collectedDate).format("DD/MM/YYYY") : ""}
                        </StyledTableCell>
                        <StyledTableCell>{data?.saving?.tenureType}</StyledTableCell>
                        <StyledTableCell>{data?.penaltyApplied}</StyledTableCell>
                        <StyledTableCell>{data?.saving?.savingStatus}</StyledTableCell>
                      </StyledTableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <Pagination
              count={totalPages}
              page={page}
              onChange={(_, newPage) => setPage(newPage)}
              variant="outlined"
              shape="rounded"
              size="large"
              style={{ marginTop: 10 }}
            />
          </Grid>
        </Grid>
      </div>
    </>
  );
};

export default withNamespaces()(SavingsCollectionReport);